import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import PRODUCT_DATA from "../data/products";
import { getCategoryBySlug, countProducts } from "../utils/helpers";
import Breadcrumb from "../components/Breadcrumb";
import HeroBanner from "../components/HeroBanner";
import SubcategoryTabs from "../components/SubcategoryTabs";
import ProductCard from "../components/ProductCard";
import useDocumentTitle from "../hooks/useDocumentTitle";
import "./CategoryPage.css";

export default function CategoryPage() {
  const { slug } = useParams();
  const [selectedTab, setSelectedTab] = useState("all");
  const [tabSlug, setTabSlug] = useState(slug);

  const result = getCategoryBySlug(PRODUCT_DATA, slug);
  const subcategories = result?.[1]?.subcategories ?? {};

  /* The selected tab is state, but the category comes from the URL. When the
     URL changes the old selection may name a subcategory the new category does
     not have — or, worse, one it coincidentally does have. Reset during render
     (not in an effect, which would run after the stale tab has already shown)
     by detecting the slug change directly, React's recommended pattern for
     resetting state when a prop changes. */
  if (slug !== tabSlug) {
    setTabSlug(slug);
    setSelectedTab("all");
  }

  const activeTab =
    selectedTab === "all" || subcategories[selectedTab] ? selectedTab : "all";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useDocumentTitle(result ? result[0] : "Category not found");

  if (!result) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🔍</div>
        <h3>Category not found</h3>
        <p>The category you are looking for does not exist.</p>
        <Link to="/" className="empty-cta">Go Home</Link>
      </div>
    );
  }

  const [categoryName, categoryData] = result;
  const { icon, banner, description } = categoryData;
  const totalCount = countProducts(subcategories);
  const subcategoryNames = Object.keys(subcategories);

  const displayProducts =
    activeTab === "all"
      ? Object.entries(subcategories).flatMap(([sub, data]) =>
          (data.products ?? []).map((p) => ({ ...p, subcategory: sub }))
        )
      : (subcategories[activeTab].products ?? []).map((p) => ({
          ...p,
          subcategory: activeTab,
        }));

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", to: "/" },
          { label: categoryName },
        ]}
      />

      <HeroBanner
        icon={icon}
        title={categoryName}
        subtitle={`${totalCount} products · ${subcategoryNames.length} subcategories`}
        banner={banner}
      />

      <section className="category-section">
        {description && <p className="category-desc">{description}</p>}

        <SubcategoryTabs
          subcategories={subcategories}
          activeTab={activeTab}
          onTabChange={setSelectedTab}
          totalCount={totalCount}
        />

        {displayProducts.length > 0 ? (
          <div className="products-grid">
            {displayProducts.map((product, i) => (
              <ProductCard
                key={`${product.subcategory}-${product.name}-${product.size}-${i}`}
                product={product}
                index={i}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No products found</h3>
            <p>Try selecting a different subcategory.</p>
          </div>
        )}
      </section>
    </>
  );
}
